import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {autorun, observable, runInAction} from 'mobx';
import {inject, observer} from 'mobx-react';
import {ChordProParser, ChordsOverWordsParser} from 'chordsheetjs';
import Chord from '@tombatossals/react-chords/lib/Chord';
import guitar from '@tombatossals/chords-db/lib/guitar.json';
import CircularLoader from '../circularLoader/CircularLoader';
import {isMobile} from '../../utils/screenUtility';
import './chordSheetViewer.scss';

const instrument = {...guitar.main, tunings: guitar.tunings};
const TOOLTIP_HEIGHT = 160;
const TOOLTIP_WIDTH = 160;
const TOOLTIP_MARGIN = 8;

@inject('looperStore')
@observer
class ChordSheetViewer extends Component {
    @observable song = null;
    @observable loading = true;
    @observable error = null;
    @observable tooltip = null; // {text, x, y}

    baseSong = null;
    pitchListener = null;

    componentDidMount() {
        this.loadAndParse();
        this.pitchListener = autorun(() => {
            const pitch = this.props.looperStore?.pitch ?? 0;
            if (this.baseSong) {
                this.renderHtml(pitch);
            }
        });
    }

    componentWillUnmount() {
        if (this.pitchListener) this.pitchListener();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.url !== this.props.url) {
            this.loadAndParse();
        }
    }

    // Escape non-standard {directives} that would break the ChordPro parser,
    // e.g. rhythm patterns like {1234}, {234}, {1…}
    sanitize(text) {
        return text.replace(/\{([^}]*)\}/g, (match, content) => {
            if (/^[a-zA-Z_\-]/.test(content.trim())) return match;
            return content;
        });
    }

    loadAndParse = async () => {
        const {url, fileName} = this.props;
        if (!url) return;

        runInAction(() => {
            this.loading = true;
            this.error = null;
        });

        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
            let text = await response.text();

            const parser = this.getParser(fileName);
            if (parser instanceof ChordProParser) {
                text = this.sanitize(text);
            }
            this.baseSong = parser.parse(text);
            const pitch = this.props.looperStore?.pitch ?? 0;
            this.renderHtml(pitch);
            runInAction(() => { this.loading = false; });
        } catch (err) {
            console.error('ChordSheetViewer error:', err);
            runInAction(() => {
                this.error = err.message;
                this.loading = false;
            });
        }
    };

    renderHtml(pitch) {
        const song = pitch !== 0 ? this.baseSong.transpose(pitch) : this.baseSong;
        runInAction(() => { this.song = song; });
    }

    renderItem(item, index) {
        if ('chords' in item) {
            return (
                <div key={index} className="column">
                    <div className="chord">{item.chords || null}</div>
                    <div className="lyrics">{item.lyrics || null}</div>
                </div>
            );
        }
        if (item.isRenderable?.()) {
            if (item.hasRenderableLabel?.()) {
                return <h3 key={index}>{item.value}</h3>;
            }
            return <div key={index} className="comment">{item.value}</div>;
        }
        return null;
    }

    renderSong(song) {
        return (
            <div className="chord-sheet">
                {song.paragraphs.map((paragraph, pi) => (
                    <div key={pi} className="paragraph">
                        {paragraph.lines
                            .filter(line => line.hasRenderableItems())
                            .map((line, li) => (
                                <div key={li} className="row">
                                    {line.items.map((item, ii) => this.renderItem(item, ii))}
                                </div>
                            ))}
                    </div>
                ))}
            </div>
        );
    }

    getParser(fileName) {
        if (fileName && /\.crd$/i.test(fileName)) {
            return new ChordsOverWordsParser();
        }
        return new ChordProParser();
    }

    lookupChord(chordName) {
        const rootMatch = chordName.match(/^[A-G][b#]?/);
        if (!rootMatch) return null;
        const root = rootMatch[0];
        const suffix = chordName.slice(root.length) || 'major';
        const chords = guitar.chords[root];
        if (!chords) return null;
        const entry = chords.find(c => c.suffix === suffix);
        return entry ? entry.positions[0] : null;
    }

    tooltipPos(rect) {
        const rawX = rect.left + rect.width / 2;
        const x = Math.max(
            TOOLTIP_WIDTH / 2 + TOOLTIP_MARGIN,
            Math.min(window.innerWidth - TOOLTIP_WIDTH / 2 - TOOLTIP_MARGIN, rawX)
        );
        const below = rect.top < TOOLTIP_HEIGHT;
        return {x, y: below ? rect.bottom : rect.top, below};
    }

    handleMouseOver = (e) => {
        const chord = e.target.closest('.chord');
        if (chord && chord.textContent.trim()) {
            const rect = chord.getBoundingClientRect();
            const {x, y, below} = this.tooltipPos(rect);
            runInAction(() => {
                this.tooltip = {
                    text: chord.textContent.trim(),
                    x, y, below,
                    position: this.lookupChord(chord.textContent.trim()),
                };
            });
        }
    };

    handleMouseOut = (e) => {
        if (!e.target.closest('.chord')) return;
        runInAction(() => { this.tooltip = null; });
    };

    handleClick = (e) => {
        const chord = e.target.closest('.chord');
        if (chord && chord.textContent.trim()) {
            const rect = chord.getBoundingClientRect();
            const {x, y, below} = this.tooltipPos(rect);
            runInAction(() => {
                this.tooltip = {
                    text: chord.textContent.trim(),
                    x, y, below,
                    position: this.lookupChord(chord.textContent.trim()),
                };
            });
        } else {
            runInAction(() => { this.tooltip = null; });
        }
    };

    render() {
        if (this.loading) return <CircularLoader/>;
        if (this.error) return <div className="chord-sheet-error">{this.error}</div>;

        const {tooltip} = this;
        return (
            <div
                className="chord-sheet-viewer"
                onClick={this.handleClick}
                onMouseOver={!isMobile() ? this.handleMouseOver : undefined}
                onMouseOut={!isMobile() ? this.handleMouseOut : undefined}
            >
                {this.song && this.renderSong(this.song)}
                {tooltip && (
                    <div
                        className={`chord-tooltip${tooltip.below ? ' chord-tooltip--below' : ''}`}
                        style={{left: tooltip.x, top: tooltip.y}}
                    >
                        {tooltip.position
                            ? <Chord chord={tooltip.position} instrument={instrument} lite={false}/>
                            : <span>{tooltip.text}</span>
                        }
                    </div>
                )}
            </div>
        );
    }
}

ChordSheetViewer.propTypes = {
    url: PropTypes.string.isRequired,
    fileName: PropTypes.string,
    looperStore: PropTypes.object
};

export default ChordSheetViewer;
